import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import JsonLd from '@/components/base/JsonLd';
import { buildGuideArticleSchema, buildGuideBreadcrumbSchema } from '@/mocks/schema';
import { mainSiteUrl } from '@/mocks/categories';

export default function GuidePage() {
  const articleSchema = buildGuideArticleSchema();
  const breadcrumbSchema = buildGuideBreadcrumbSchema();

  return (
    <div className="min-h-screen bg-background-50">
      <JsonLd data={articleSchema} />
      <JsonLd data={breadcrumbSchema} />
      <Navbar />

      <main>
        <article className="pt-24 pb-16">
          <div className="w-full max-w-[800px] mx-auto px-4 md:px-6">
            <nav className="mb-8" aria-label="Breadcrumb">
              <ol className="flex items-center gap-2 text-sm text-foreground-500">
                <li>
                  <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">
                    Home
                  </Link>
                </li>
                <li>
                  <i className="ri-arrow-right-s-line text-xs"></i>
                </li>
                <li className="text-foreground-800 font-medium">
                  Complete ACBuy Spreadsheet Guide 2026
                </li>
              </ol>
            </nav>

            <header className="mb-10">
              <div className="flex items-center gap-2 mb-4">
                <span className="px-3 py-1 bg-primary-100 text-primary-700 text-xs font-semibold rounded-full">
                  Updated June 2026
                </span>
                <span className="text-xs text-foreground-500">•</span>
                <span className="text-xs text-foreground-500">10 min read</span>
              </div>
              <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight">
                Complete <strong>ACBuy Spreadsheet Guide 2026</strong> — How to Use ACBuySpreadsheet Effectively
              </h1>
              <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed">
                Everything you need to know about navigating the <strong>acbuy spreadsheet</strong> directory — from getting started to advanced shopping strategies. This comprehensive guide covers all 14 categories, safety tips, and how to find the <strong>best acbuy spreadsheet</strong> deals in 2026.
              </p>
              <div className="mt-6 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                  <span className="text-primary-600 text-sm font-bold">AE</span>
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground-900">ACBuySpreadsheet Editorial Team</p>
                  <p className="text-xs text-foreground-500">Fact Checked • Reviewed by Shopping Experts</p>
                </div>
              </div>
            </header>

            <div className="mb-10 rounded-2xl overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=Modern%20workspace%20with%20laptop%20displaying%20clean%20dashboard%20interface%2C%20surrounded%20by%20fashion%20catalog%20items%20and%20coffee%20on%20warm%20oak%20desk%2C%20soft%20natural%20daylight%20from%20window%2C%20editorial%20photography%2C%20organized%20composition%2C%20warm%20neutral%20background%2C%20professional%20yet%20cozy%20atmosphere%2C%20no%20visible%20screen%20text&width=1400&height=600&seq=guide-article-hero-v2&orientation=landscape"
                alt="Complete ACBuy Spreadsheet Guide 2026"
                className="w-full h-auto object-cover"
              />
            </div>

            <div className="prose-custom">
              <section id="get-started" className="mb-12">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
                  What Is ACBuySpreadsheet?
                </h2>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                  ACBuySpreadsheet is a dedicated directory platform that organizes and curates product links from across the ACBuy ecosystem. Think of it as your personal shopping assistant — rather than spending hours manually searching through countless pages, the <strong>acbuy spreadsheet</strong> directory brings everything together in one clean, organized interface. With over 5,000 verified product links across 14 distinct categories, it has quickly become the go-to resource for shoppers who want efficiency and reliability.
                </p>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                  The platform was created to solve a common problem: finding specific products across fragmented marketplaces is time-consuming and frustrating. By centralizing links and categorizing them thoughtfully — Shoes, Hoodies, T-Shirts, Jackets, and more — ACBuySpreadsheet eliminates the guesswork. Whether you are a first-time user or a seasoned shopper, the directory structure makes navigation intuitive and fast.
                </p>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                  One of the key advantages of using the <strong>best acbuy spreadsheet</strong> is that it is continuously maintained. Unlike static lists that become outdated within weeks, ACBuySpreadsheet is refreshed on a weekly basis, ensuring every link leads to active, valid product pages. This commitment to freshness is what sets ACBuySpreadsheet apart from manual searching and outdated directories.
                </p>
              </section>

              <section className="mb-12">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
                  Why ACBuySpreadsheet Matters for Online Shoppers
                </h2>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                  In 2026, the online shopping landscape is more crowded than ever. With countless platforms, sellers, and product listings, finding exactly what you need can feel overwhelming. This is where a well-structured <strong>acbuy spreadsheet guide</strong> becomes invaluable. ACBuySpreadsheet provides a curated experience that filters out noise and presents only verified, organized product links.
                </p>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                  Consider the alternative: manually browsing through dozens of pages, comparing prices across different sections, and trying to remember which category had that one jacket you liked. With ACBuySpreadsheet, all of this information is structured and accessible from a single starting point. The time savings alone make it worthwhile — most users report finding what they need in under two minutes compared to twenty minutes or more with manual searching.
                </p>
                <div className="bg-background-100 rounded-xl p-5 md:p-6 my-6">
                  <h3 className="text-sm font-semibold text-foreground-900 mb-3">
                    Key Benefits at a Glance
                  </h3>
                  <ul className="space-y-2">
                    {[
                      '5,000+ verified product links across 14 categories',
                      'Weekly updates ensure all links remain active',
                      'No registration or payment required — completely free',
                      'Comprehensive safety and QC information included',
                      'Expert buying guides for beginners and advanced shoppers',
                      'Direct links to KASpreadsheet for seamless shopping',
                    ].map((benefit) => (
                      <li key={benefit} className="flex items-start gap-2 text-sm text-foreground-700">
                        <i className="ri-check-line text-primary-500 mt-0.5 flex-shrink-0"></i>
                        <span>{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </section>

              <section className="mb-12">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
                  How to Get Started with ACBuy Spreadsheet
                </h2>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                  Getting started with ACBuySpreadsheet is remarkably straightforward. The platform has been designed with simplicity in mind, requiring no sign-ups, no downloads, and no technical knowledge. Here is a step-by-step walkthrough to help you navigate the <strong>acbuy spreadsheet</strong> directory like a pro.
                </p>

                <div className="space-y-5 mt-6">
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary-500 text-background-50 flex items-center justify-center text-sm font-bold">
                      1
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-900 mb-1">Visit the Homepage</h3>
                      <p className="text-sm text-foreground-600">
                        Head to the <Link to="/" className="text-primary-500 hover:underline cursor-pointer">ACBuySpreadsheet homepage</Link> where you will find the category dropdown, featured categories, and quick-access buttons to the main KASpreadsheet website.
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary-500 text-background-50 flex items-center justify-center text-sm font-bold">
                      2
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-900 mb-1">Browse Categories</h3>
                      <p className="text-sm text-foreground-600">
                        Use the Categories dropdown in the navigation bar or scroll through the category grid on the homepage. Each category — from Shoes to Perfumes — contains curated product links organized for easy browsing.
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary-500 text-background-50 flex items-center justify-center text-sm font-bold">
                      3
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-900 mb-1">Click Through to Products</h3>
                      <p className="text-sm text-foreground-600">
                        Select any category to open the corresponding page on KASpreadsheet in a new tab. From there, browse individual products, compare options, and make your purchase decisions with confidence.
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary-500 text-background-50 flex items-center justify-center text-sm font-bold">
                      4
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-900 mb-1">Read Buying Guides</h3>
                      <p className="text-sm text-foreground-600">
                        Check out our <Link to="/acbuy-spreadsheet-guide#safety" className="text-primary-500 hover:underline cursor-pointer">safety and QC guides</Link> to ensure you are making informed purchasing decisions. These guides cover everything from verifying product quality to understanding shipping timelines.
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              <section className="mb-12">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
                  Best ACBuy Spreadsheet Categories Explained
                </h2>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                  One of the standout features of ACBuySpreadsheet is its comprehensive category system. Each of the 14 categories represents a distinct product type, making it easy to find exactly what you are looking for. Here is a closer look at some of the most popular categories and what makes them worth exploring.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                  {[
                    { name: 'Shoes', desc: 'From athletic sneakers to casual footwear, the Shoes category offers the widest variety of styles. A must-visit for sneaker enthusiasts looking for the latest drops and classic designs.', highlight: 'Most Popular' },
                    { name: 'Hoodies & Sweaters', desc: 'Perfect for seasonal shopping, this category features cozy hoodies and knit sweaters. The selection is refreshed frequently with new arrivals and trending styles.', highlight: 'Trending' },
                    { name: 'Jackets', desc: 'Outerwear for every occasion — lightweight windbreakers, stylish bombers, and warm winter coats. Quality and variety make this a top category for savvy shoppers.', highlight: 'Premium' },
                    { name: 'Accessories', desc: 'The finishing touches that complete any outfit. Watches, bracelets, sunglasses, and more — organized in one convenient location for easy browsing.', highlight: 'Essential' },
                  ].map((cat) => (
                    <div key={cat.name} className="bg-background-100 rounded-xl p-5">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-sm font-semibold text-foreground-900">{cat.name}</h3>
                        <span className="text-[10px] font-semibold text-primary-600 bg-primary-50 px-2 py-0.5 rounded-full">
                          {cat.highlight}
                        </span>
                      </div>
                      <p className="text-sm text-foreground-600 leading-relaxed">{cat.desc}</p>
                    </div>
                  ))}
                </div>
              </section>

              <section id="safety" className="mb-12">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
                  ACBuySpreadsheet Safety Tips for 2026
                </h2>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                  Safety is a top priority when shopping through any directory. While ACBuySpreadsheet verifies all links before adding them, it is always wise to follow basic safety practices. Here are our expert-recommended tips for a secure shopping experience with the <strong>acbuy spreadsheet</strong>.
                </p>

                <div className="bg-accent-50 border border-accent-200 rounded-xl p-5 md:p-6 my-6">
                  <div className="flex items-start gap-3 mb-4">
                    <div className="w-8 h-8 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-shield-check-line text-accent-600"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-900">Verify Product Details Before Purchasing</h3>
                      <p className="text-sm text-foreground-600 mt-1">
                        Always review product descriptions, images, and specifications carefully. Compare listings when possible to ensure you are getting accurate information about the item you intend to purchase.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 mb-4">
                    <div className="w-8 h-8 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-shield-check-line text-accent-600"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-900">Check QC Photos When Available</h3>
                      <p className="text-sm text-foreground-600 mt-1">
                        Quality Control photos give you a realistic view of the product before it ships. Request QC images whenever possible to verify the item matches the listing description.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 mb-4">
                    <div className="w-8 h-8 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-shield-check-line text-accent-600"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-900">Understand Shipping Timelines</h3>
                      <p className="text-sm text-foreground-600 mt-1">
                        Shipping times can vary significantly. Familiarize yourself with estimated delivery windows before placing an order to avoid unexpected delays.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-shield-check-line text-accent-600"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-900">Use Trusted Payment Methods</h3>
                      <p className="text-sm text-foreground-600 mt-1">
                        Stick to secure, traceable payment methods for all transactions. Avoid sharing sensitive financial information outside of established payment platforms.
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              <section className="mb-12">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
                  ACBuy Spreadsheet vs Traditional Shopping
                </h2>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                  The difference between using an organized <strong>acbuy spreadsheet</strong> directory and traditional manual searching is dramatic. With manual searching, shoppers typically visit multiple pages, lose track of items they were interested in, and spend significant time just navigating between sections. ACBuySpreadsheet eliminates all of these friction points.
                </p>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                  The directory approach also enables price comparison, trend discovery, and category exploration that is simply not possible with ad-hoc searching. When you can see all available categories at a glance and jump directly to organized product listings, the entire shopping experience becomes more efficient and enjoyable.
                </p>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                  For 2026, the data is clear: organized directories like ACBuySpreadsheet are the future of online shopping. They save time, reduce frustration, and help shoppers make better-informed decisions. As the <strong>best acbuy spreadsheet</strong> resource available, ACBuySpreadsheet continues to set the standard for what a product directory should be.
                </p>
              </section>

              <section className="mb-12">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
                  Frequently Asked Questions
                </h2>
                <div className="space-y-4">
                  {[
                    {
                      q: 'How do I know the links on ACBuySpreadsheet are legitimate?',
                      a: 'Every link in the ACBuySpreadsheet directory is manually verified by our editorial team before being published. We also conduct weekly audits to ensure all links remain active and point to legitimate product pages. If you ever encounter an issue, the directory is refreshed regularly to maintain quality.',
                    },
                    {
                      q: 'Can I suggest a category to be added?',
                      a: 'ACBuySpreadsheet is continuously evolving based on user needs. While we currently maintain 14 carefully curated categories, we are always open to expanding the directory. The most effective way to share feedback is through the KASpreadsheet platform, where our team monitors user suggestions.',
                    },
                    {
                      q: 'Is there a mobile version of ACBuySpreadsheet?',
                      a: 'Yes! ACBuySpreadsheet is fully responsive and works seamlessly across desktop, tablet, and mobile devices. The navigation, category browsing, and all features are optimized for every screen size, so you can shop on the go without any issues.',
                    },
                  ].map((faq, idx) => (
                    <div key={idx} className="bg-background-100 rounded-xl p-5">
                      <h3 className="text-sm font-semibold text-foreground-900 mb-2">{faq.q}</h3>
                      <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                    </div>
                  ))}
                </div>
              </section>

              <section className="mb-12 bg-primary-50 rounded-2xl p-6 md:p-8 text-center">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-3">
                  Ready to Start Shopping?
                </h2>
                <p className="text-sm md:text-base text-foreground-600 mb-6 max-w-lg mx-auto">
                  Now that you have the complete <strong>acbuy spreadsheet guide</strong>, you are ready to explore the directory and discover the easiest way to shop smarter. Head back to the homepage or jump directly to KASpreadsheet.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                  <Link
                    to="/"
                    className="px-6 py-3 bg-primary-500 text-background-50 text-sm font-semibold rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    ← Back to Homepage
                  </Link>
                  <a
                    href={mainSiteUrl}
                    target="_blank"
                    rel="noopener nofollow"
                    className="px-6 py-3 bg-foreground-900 text-background-50 text-sm font-semibold rounded-full hover:bg-foreground-800 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Shop on KASpreadsheet →
                  </a>
                </div>
              </section>

              <section className="border-t border-background-200/70 pt-8">
                <h3 className="text-sm font-semibold text-foreground-900 mb-4">
                  Read Next
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {[
                    { label: '← Back to Homepage', path: '/' },
                    { label: 'Browse All Categories', path: '/#categories' },
                    { label: 'Safety Tips & QC Guide', path: '/acbuy-spreadsheet-guide#safety' },
                    { label: 'Getting Started Guide', path: '/acbuy-spreadsheet-guide#get-started' },
                    { label: 'View FAQ Section', path: '/#faq' },
                    { label: 'Why Use ACBuySpreadsheet', path: '/#why' },
                  ].map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      className="flex items-center gap-2 px-4 py-3 bg-background-100 rounded-xl text-sm text-foreground-700 hover:text-primary-500 hover:bg-primary-50 transition-colors cursor-pointer"
                    >
                      <i className="ri-arrow-right-line text-primary-400"></i>
                      {link.label}
                    </Link>
                  ))}
                </div>
              </section>
            </div>
          </div>
        </article>
      </main>

      <Footer />
    </div>
  );
}